import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Instagram, MessageCircle, Send, Check } from "lucide-react";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import { contactInfo } from "../data/projects";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Failed to send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell testId="page-contact">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-4 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
            Contact
          </div>
          <h1 className="font-fraunces text-5xl md:text-7xl font-light text-slate-900 dark:text-slate-100 leading-[1.05] max-w-3xl text-balance">
            Let&apos;s make something you&apos;ll love.
          </h1>
          <p className="mt-6 font-manrope text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
            I specialize in portrait, documentary, and product photography.
            Whether you need authentic storytelling, striking portraits, or
            clean commercial visuals, I&apos;d love to bring your vision to life.
            Get in touch, and let&apos;s create something amazing together.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="glass-strong rounded-[32px] p-8 md:p-10 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Name"
                  testId="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                />
                <Field
                  label="Email"
                  testId="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@domain.com"
                />
              </div>
              <Field
                label="Subject"
                testId="contact-subject"
                value={form.subject}
                onChange={update("subject")}
                placeholder="What's it about?"
              />
              <div>
                <label className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-500 dark:text-slate-400 dark:text-slate-500 mb-2 block">
                  Message
                </label>
                <textarea
                  data-testid="contact-message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell me a little about the project…"
                  className="w-full rounded-2xl border border-[#C6E2FF]/60 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] backdrop-blur-sm px-5 py-4 font-manrope text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#C6E2FF] focus:border-transparent transition"
                />
              </div>

              <div className="flex items-center justify-between gap-4 pt-2">
                <p className="font-manrope text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                  Or email me directly at{" "}
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-slate-800 dark:text-slate-200 underline underline-offset-2"
                  >
                    {contactInfo.email}
                  </a>
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-6 py-3 font-manrope text-sm font-medium tracking-wide hover:bg-slate-800 dark:hover:bg-slate-100 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sent ? (
                    <>
                      <Check size={16} /> Sent
                    </>
                  ) : (
                    <>
                      {submitting ? "Sending…" : "Send message"} <Send size={14} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Details */}
          <div className="lg:col-span-5 space-y-4" data-testid="contact-details">
            <ContactRow
              icon={<Mail size={16} />}
              label="Email"
              value={contactInfo.email}
              href={`mailto:${contactInfo.email}`}
              testId="contact-detail-email"
            />
            <ContactRow
              icon={<Instagram size={16} />}
              label="Instagram"
              value={contactInfo.instagram}
              href={contactInfo.instagramUrl}
              external
              testId="contact-detail-instagram"
            />
            <ContactRow
              icon={<MessageCircle size={16} />}
              label="WhatsApp"
              value={contactInfo.whatsapp}
              href={contactInfo.whatsappUrl}
              external
              testId="contact-detail-whatsapp"
            />

            <div className="glass rounded-[24px] p-6 mt-8">
              <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-3">
                Response time
              </div>
              <p className="font-manrope text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                I usually reply within 1–2 working days. For time-sensitive
                briefs, WhatsApp is fastest.
              </p>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

const Field = ({ label, value, onChange, placeholder, type = "text", testId }) => (
  <div>
    <label className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-500 dark:text-slate-400 dark:text-slate-500 mb-2 block">
      {label}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full rounded-full border border-[#C6E2FF]/60 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] backdrop-blur-sm px-5 py-3 font-manrope text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#C6E2FF] focus:border-transparent transition"
    />
  </div>
);

const ContactRow = ({ icon, label, value, href, external, testId }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noreferrer" : undefined}
    data-testid={testId}
    className="glass rounded-[24px] p-5 flex items-center gap-4 hover:bg-white/80 transition group"
  >
    <div className="shrink-0 w-11 h-11 rounded-2xl bg-[#E8F4FF] dark:bg-white/[0.08] border border-[#C6E2FF]/50 dark:border-white/10 grid place-items-center text-slate-700 dark:text-slate-200">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500">
        {label}
      </div>
      <div className="font-fraunces text-lg text-slate-900 dark:text-slate-100 mt-0.5 group-hover:text-slate-700 dark:text-slate-200 transition break-words">
        {value}
      </div>
    </div>
  </a>
);

export default Contact;
